/**
 * CostSchemeContent — the orchestrator of the Cost Scheme agenda once the schema is loaded.
 *
 * Owns the editable board state ({@link useCostSchemeBoard}), the view mode (board/list) and drawer
 * selection, and composes the toolbar, the active view, the autosave action bar, the detail drawer and the
 * override confirm dialogs. Server sync (save / approve / inheritance override) is delegated to
 * {@link useCostSchemeSync}; a term whose schema is inherited renders read-only with a create-override banner.
 */
import { useMemo, useState } from 'react';

import type { GetProgramSchemeResponse, SerieLayerStateItem } from '@/api/generated/schemas';
import { LayerState, SerieLayer } from '@/api/generated/schemas';
import { AutoSaveFormActionBar, ConfirmDialog } from '@/shared/ui';
import { useTranslation } from 'react-i18next';
import { ApproveLayerButton } from '../ApproveLayerButton';
import { CostSchemeBoard } from './board';
import { CostSchemeList } from './list';
import { CostSchemeTermsPanel } from './terms';
import { CostSchemeDrawer } from './drawer';
import { CostSchemeToolbar } from './CostSchemeToolbar';
import { CostSchemeInheritBanner } from './CostSchemeInheritBanner';
import { buildRateLookup, computeCostTotals } from './model';
import { useCostSchemeBoard } from './useCostSchemeBoard';
import { useCostSchemeStats } from './useCostSchemeStats';
import { useCostSchemeSync } from './useCostSchemeSync';
import './costScheme.css';

type Scope = 'serie' | 'term';

interface CostSchemeContentProps {
  scope: Scope;
  productId: string;
  serieId: string;
  termId?: string;
  /** Vrstvy schvalované entity (seriálu / termínu) – vstup pro schvalovací tlačítko v patičce. */
  layers: SerieLayerStateItem[];
  /** Vrstvy seriálu – zdroj nabídky „schválit až po X“ (jen ve scope termínu). */
  serieLayers?: SerieLayerStateItem[];
  onBack: () => void;
  response: GetProgramSchemeResponse;
}

export function CostSchemeContent({
  scope,
  productId,
  serieId,
  termId,
  layers,
  serieLayers,
  onBack,
  response,
}: CostSchemeContentProps) {
  const { t } = useTranslation('venua-planner');
  const board = useCostSchemeBoard(response);

  const [viewMode, setViewMode] = useState<'board' | 'list'>('board');
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<'createOverride' | 'removeOverride' | null>(null);

  const isInherited = scope === 'term' && response.isInherited;
  // Board je read-only, když termín dědí schéma ze série, nebo když je vrstva Product cost scheme
  // připravená / schválená (v obou případech se needituje, jen se mění stav vrstvy).
  const productCostState = layers.find(
    (entry) => entry.layer === SerieLayer.ProductCostScheme
  )?.state;
  const layerReadOnly =
    productCostState === LayerState.Prepared || productCostState === LayerState.Approved;
  const readOnly = isInherited || layerReadOnly;

  const sync = useCostSchemeSync({ scope, productId, serieId, termId, board, readOnly });
  const stats = useCostSchemeStats(board.model);

  // Živý souhrn nákladů v domácí měně (přepočet dle fixovaného kurzovního lístku série z GET response).
  const rateLookup = useMemo(
    () => buildRateLookup(response.domesticCurrency, response.exchangeRates),
    [response.domesticCurrency, response.exchangeRates]
  );
  const costTotals = useMemo(
    () => computeCostTotals(board.model, rateLookup, response.pax),
    [board.model, rateLookup, response.pax]
  );

  const selectedPart = useMemo(
    () => board.model.parts.find((p) => p.uid === selectedUid) ?? null,
    [board.model.parts, selectedUid]
  );

  const handleConfirm = () => {
    const action = confirm;
    setConfirm(null);
    if (action) void sync.runOverride(action);
  };

  const rootStyle = {
    ['--tp-day-count' as string]: board.model.lengthDays,
    ['--tp-night-count' as string]: Math.max(1, stats.totalNights),
  } as React.CSSProperties;

  return (
    <div className="text-phx-text relative h-full min-h-0 [font-family:var(--phx-font-sans)]">
      <div className="bg-phx-bg flex h-full flex-col" style={rootStyle}>
        <CostSchemeToolbar
          t={t}
          totals={costTotals}
          pax={response.pax}
          exchangeRates={response.exchangeRates}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          onUndo={board.undo}
          undoDisabled={!board.canUndo || readOnly}
          onRedo={board.redo}
          redoDisabled={!board.canRedo || readOnly}
          showRevert={scope === 'term' && !isInherited}
          onRevert={() => setConfirm('removeOverride')}
          revertDisabled={sync.overridePending}
        />

        {isInherited ? (
          <CostSchemeInheritBanner
            t={t}
            onCreateOverride={() => setConfirm('createOverride')}
            createDisabled={sync.overridePending}
          />
        ) : null}

        {board.model.lengthDays < 1 ? (
          <div className="bg-phx-bg block min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
            <div className="tp-list-empty p-phx-8">{t('costScheme.noLength')}</div>
          </div>
        ) : viewMode === 'list' ? (
          <div className="flex min-h-0 flex-1 overflow-hidden">
            <div className="bg-phx-bg block min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
              <CostSchemeList
                model={board.model}
                readOnly={readOnly}
                onOpenPart={setSelectedUid}
                onUpdatePart={board.updatePart}
              />
            </div>
            {scope === 'serie' ? (
              <CostSchemeTermsPanel
                productId={productId}
                serieId={serieId}
                liveTotals={costTotals}
                pax={response.pax}
              />
            ) : null}
          </div>
        ) : (
          <CostSchemeBoard
            board={board}
            readOnly={readOnly}
            onOpenPart={setSelectedUid}
            selectedUid={selectedUid}
          />
        )}

        <AutoSaveFormActionBar
          onSave={sync.autosaveFlush}
          isDirty={board.dirty}
          changeToken={board.changeToken}
          onBack={onBack}
          disabled={readOnly}
          extraActions={
            <ApproveLayerButton
              currentLayer={SerieLayer.ProductCostScheme}
              layers={layers}
              serieLayers={serieLayers}
              onChangeState={sync.changeState}
              onBeforeAction={sync.saveBeforeAction}
              loading={sync.changing}
              disabled={isInherited}
            />
          }
        />

        <CostSchemeDrawer
          part={selectedPart}
          readOnly={readOnly}
          onClose={() => setSelectedUid(null)}
          onUpdatePart={board.updatePart}
          onDeletePart={board.deletePart}
        />

        {confirm ? (
          <ConfirmDialog
            title={
              confirm === 'createOverride'
                ? t('costScheme.confirmOverride.title')
                : t('costScheme.confirmRevert.title')
            }
            message={
              confirm === 'createOverride'
                ? t('costScheme.confirmOverride.message')
                : t('costScheme.confirmRevert.message')
            }
            confirmVariant="primary"
            confirmLabel={
              confirm === 'createOverride'
                ? t('costScheme.createOverride')
                : t('costScheme.revertToInheritance')
            }
            cancelLabel={t('costScheme.cancel')}
            onConfirm={handleConfirm}
            onCancel={() => setConfirm(null)}
          />
        ) : null}
      </div>
    </div>
  );
}
